import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  billingApi,
  formatCents,
  type BillingStatus,
  type PlanTier,
  type Plans,
  type TierKey,
} from '@/services/billingApi';
import { ApiClientError } from '@/services/apiClient';

type LoadStatus = 'idle' | 'loading' | 'ready' | 'error';

const CAP_LABEL_KEYS: Record<string, string> = {
  media: 'billing.caps.media',
  sports: 'billing.caps.sports',
  lobbies: 'billing.caps.lobbies',
  downloads: 'billing.caps.downloads',
};

function tierRank(tier: TierKey): number {
  return tier === 'tier2' ? 2 : tier === 'tier1' ? 1 : 0;
}

export function SubscribePage(): JSX.Element {
  const { t } = useTranslation();
  const [plans, setPlans] = useState<Plans | null>(null);
  const [me, setMe] = useState<BillingStatus | null>(null);
  const [status, setStatus] = useState<LoadStatus>('idle');
  const [busy, setBusy] = useState<TierKey | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [params] = useSearchParams();
  const cancelled = params.get('checkout') === 'cancelled';

  useEffect(() => {
    let active = true;
    setStatus('loading');
    Promise.all([billingApi.getPlans(), billingApi.getMe().catch(() => null)])
      .then(([p, m]) => {
        if (!active) return;
        setPlans(p);
        setMe(m);
        setStatus('ready');
      })
      .catch(() => active && setStatus('error'));
    return () => {
      active = false;
    };
  }, []);

  async function buy(tier: TierKey): Promise<void> {
    setBusy(tier);
    setError(null);
    try {
      const { url } = await billingApi.createCheckout(tier);
      window.location.href = url;
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 503) {
        setError(t('billing.subscribe.checkoutSoon'));
      } else {
        setError(err instanceof Error ? err.message : t('billing.subscribe.checkoutError'));
      }
      setBusy(null);
    }
  }

  const ownedTier = me?.ownedTier ?? null;
  const tier3Active = me?.tier3Active ?? false;
  const priceOf = (key: TierKey): number => plans?.tiers.find((t) => t.key === key)?.priceCents ?? 0;

  /** What the CTA for a given tier card should say + whether it's actionable. */
  function cta(tier: PlanTier): { label: string; disabled: boolean; note?: string } {
    if (tier.key === 'tier3') {
      if (tier3Active) return { label: t('billing.cta.active'), disabled: true };
      if (!ownedTier) return { label: t('billing.cta.requiresBase'), disabled: true, note: t('billing.cta.requiresBaseNote') };
      return { label: t('billing.cta.subscribe', { price: formatCents(tier.priceCents) }), disabled: false };
    }
    // One-time tiers.
    if (ownedTier && tierRank(ownedTier as TierKey) >= tierRank(tier.key)) {
      return { label: ownedTier === tier.key ? t('billing.cta.owned') : t('billing.cta.included'), disabled: true };
    }
    if (ownedTier === 'tier1' && tier.key === 'tier2') {
      const delta = priceOf('tier2') - priceOf('tier1');
      return { label: t('billing.cta.upgrade', { price: formatCents(delta) }), disabled: false, note: t('billing.cta.upgradeNote') };
    }
    return { label: t('billing.cta.buy', { price: formatCents(tier.priceCents) }), disabled: false };
  }

  return (
    <main className="admin-page">
      <header className="admin-page__header">
        <h1>{t('billing.subscribe.title')}</h1>
        <p className="muted">{t('billing.subscribe.blurb')}</p>
      </header>

      {cancelled && (
        <p className="muted" role="status">{t('billing.subscribe.cancelled')}</p>
      )}
      {status === 'loading' && <p className="muted" role="status">{t('billing.subscribe.loading')}</p>}
      {status === 'error' && <p className="error" role="alert">{t('billing.subscribe.loadError')}</p>}

      {status === 'ready' && plans && (
        <section className="plan-grid">
          {plans.tiers.map((tier) => {
            const action = cta(tier);
            return (
              <article className="plan-card admin-card" key={tier.key} data-testid={`plan-${tier.key}`}>
                <h2 className="admin-card__title">{tier.label}</h2>
                <div className="plan-price-lg">
                  {formatCents(tier.priceCents)}
                  <span className="muted">{tier.oneTime ? t('billing.subscribe.once') : t('billing.subscribe.perMonth')}</span>
                </div>
                <ul className="plan-caps">
                  {tier.capabilities.map((c) => (
                    <li key={c}>{CAP_LABEL_KEYS[c] ? t(CAP_LABEL_KEYS[c]) : c}</li>
                  ))}
                </ul>
                <button
                  type="button"
                  className="btn"
                  disabled={action.disabled || busy === tier.key}
                  onClick={() => void buy(tier.key)}
                  data-testid={`buy-${tier.key}`}
                >
                  {busy === tier.key ? t('billing.subscribe.starting') : action.label}
                </button>
                {action.note && <p className="muted" style={{ marginTop: 6 }}>{action.note}</p>}
              </article>
            );
          })}
        </section>
      )}

      {plans && !plans.checkoutEnabled && (
        <p className="muted" role="status" style={{ marginTop: 12 }}>
          {t('billing.subscribe.comingSoon')}
        </p>
      )}
      {error && <p className="error" role="alert" style={{ marginTop: 12 }}>{error}</p>}
    </main>
  );
}
